import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useContentSetting } from "./Hooks/ContentSetting";
import { isCertificatesCapability } from "./utils/associateCapabilities";
import DashboardLayout from "./layouts/dashboard";
import LogoOnlyLayout from "./layouts/LogoOnlyLayout";
import DashboardAuthGuard from "./components/auth/DashboardAuthGuard";
import AppLoader from "./components/GeneralComponents/AppLoader";

const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"));
const HolyQuranIndexes = lazy(() => import("./pages/LessonBooks/HolyQuranIndexes"));
const HolyQuranIndexMainPage = lazy(() => import("./pages/LessonBooks/HolyQuranIndexMainPage"));
const HolyQuranIndexPages = lazy(() => import("./pages/LessonBooks/HolyQuranIndexPages"));
const Gallery = lazy(() => import("./pages/LessonBooks/Gallery"));
const Login = lazy(() => import("./pages/Login/Login"));
const LoginRedirect = lazy(() => import("./pages/Login/LoginRedirect"));
const AuthCallback = lazy(() => import("./pages/Auth/AuthCallback"));
const QuidaEnglishIndexMainPage = lazy(() => import("./pages/LessonBooks/QuidaEnglishIndexMainPage"));
const QuidaEnglishIndexPages = lazy(() => import("./pages/LessonBooks/QuidaEnglishIndexPages"));
const StudentsList = lazy(() => import("./pages/Students/StudentsList"));
const PersonalNotes = lazy(() => import("./pages/PersonalNotes/PersonalNotes"));
const Chatting = lazy(() => import("./pages/Chatting/Chatting"));
const CallsList = lazy(() => import("./pages/Chatting/CallsList"));
const TeachersList = lazy(() => import("./pages/Teachers/TeachersList"));
const StudentsListByTeacher = lazy(() => import("./pages/Teachers/StudentsListByTeacher"));
const StudentPortalSetting = lazy(() => import("./pages/Settings/StudentPortalSetting"));
const TeacherPortalSetting = lazy(() => import("./pages/Settings/TeacherPortalSetting"));
const TrainingLesson = lazy(() => import("./pages/TrainingLesson/TrainingLesson"));
const AdminTrainingLesson = lazy(() => import("./pages/TrainingLesson/AdminTrainingLesson"));
const ToDoLists = lazy(() => import("./pages/ToDoList/ToDoLists"));
const StudentReports = lazy(() => import("./pages/StudentReports/StudentReports"));
const ClientsList = lazy(() => import("./pages/Clients/ClientsList"));
const EmailSetting = lazy(() => import("./pages/Settings/EmailSetting"));
const Feeds = lazy(() => import("./pages/Feeds/Feeds"));
const Examination = lazy(() => import("./pages/Examinations/Examination"));
const ExamResult = lazy(() => import("./pages/Examinations/ExamResult"));
const ReportedFeeds = lazy(() => import("./pages/Feeds/ReportedFeeds"));
const Classes = lazy(() => import("./pages/Chatting/Classes"));
const ClassPeriods = lazy(() => import("./pages/Chatting/ClassPeriods"));
const ClassAttendancesList = lazy(() => import("./pages/Chatting/ClassAttendancesList"));
const ClassAttendanceAdd = lazy(() => import("./pages/Chatting/ClassAttendanceAdd"));
const ClassStudentLessonsList = lazy(() => import("./pages/Chatting/ClassStudentLessonsList"));
const Subjects = lazy(() => import("./pages/Chatting/Subjects"));
const Lessons = lazy(() => import("./pages/Chatting/Lessons"));
const LessonDetail = lazy(() => import("./pages/Chatting/LessonDetail"));
const ManageClassesByTeacher = lazy(
  () => import("./pages/Teachers/ManageClassesByTeacher"),
);
const ManageSubjectsByTeacher = lazy(
  () => import("./pages/Teachers/ManageSubjectsByTeacher"),
);
const ManageClassesByStudent = lazy(
  () => import("./pages/Students/ManageClassesByStudent"),
);
const ManageSubjectsByStudent = lazy(
  () => import("./pages/Students/ManageSubjectsByStudent"),
);
const ManageTeachersByStudent = lazy(
  () => import("./pages/Students/ManageTeachersByStudent"),
);
const ManageStudentsByClass = lazy(
  () => import("./pages/Classes/ManageStudentsByClass"),
);
const BooksList = lazy(() => import("./pages/Library/BooksList"));
const BookDetail = lazy(() => import("./pages/Library/BookDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

const CertificateTemplates = lazy(() => import("./pages/Certificates/CertificateTemplates"));
const CertificateTemplateForm = lazy(() => import("./pages/Certificates/CertificateTemplateForm"));
const CertificateIssue = lazy(() => import("./pages/Certificates/CertificateIssue"));
const CertificatesBulkIssue = lazy(() => import("./pages/Certificates/CertificatesBulkIssue"));
const CertificatesList = lazy(() => import("./pages/Certificates/CertificatesList"));
const MyCertificates = lazy(() => import("./pages/Certificates/MyCertificates"));
const VerifyCertificate = lazy(() => import("./pages/Certificates/VerifyCertificate"));

function CertificateFeatureRouteGuard() {
  const { userInfo } = useContentSetting();
  if (userInfo && userInfo._id && !isCertificatesCapability(userInfo)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

function RouteFallback() {
  return <AppLoader minHeight="40vh" size="medium" />;
}

export default function Router() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route element={<DashboardAuthGuard />}>
      <Route element={<DashboardLayout />}>
        <Route path="/welcome" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/lesson-books" element={<Gallery />} />
        <Route path="/lesson-books/quran-pak" element={<HolyQuranIndexes />} />
        <Route
          path="/lesson-books/quran-pak/:index"
          element={<HolyQuranIndexPages />}
        />
        <Route
          path="/lesson-books/quran-pak/:index/:page_index"
          element={<HolyQuranIndexMainPage />}
        />
        <Route
          path="/lesson-books/quida-english"
          element={<QuidaEnglishIndexPages />}
        />
        <Route
          path="/lesson-books/quida-english/:page_index"
          element={<QuidaEnglishIndexMainPage />}
        />
        <Route path="/students" element={<StudentsList />} />
        <Route path="/class-room" element={<Chatting />} />
        <Route path="/class-calls" element={<CallsList />} />
        <Route path="/classes" element={<Classes />} />
        <Route path="/class-periods" element={<ClassPeriods />} />
        <Route path="/classes/:classId/attendances" element={<ClassAttendancesList />} />
        <Route path="/classes/:classId/attendances/add" element={<ClassAttendanceAdd />} />
        <Route path="/classes/:classId/student-lessons" element={<ClassStudentLessonsList />} />
        <Route
          path="/classes/manage-students/:class_id"
          element={<ManageStudentsByClass />}
        />
        <Route path="/subjects" element={<Subjects />} />
        <Route path="/subjects/:subjectId/lessons" element={<Lessons />} />
        <Route path="/subjects/:subjectId/lessons/:lessonId" element={<LessonDetail />} />
        <Route path="/training-lesson" element={<TrainingLesson />} />
        <Route
          path="/admin-training-lesson"
          element={<AdminTrainingLesson />}
        />
        <Route path="/teachers" element={<TeachersList />} />
        <Route path="/guardians" element={<ClientsList />} />
        <Route
          path="/teachers/students-list/:teacher_id"
          element={<StudentsListByTeacher />}
        />
        <Route
          path="/teachers/manage-classes-access/:teacher_id"
          element={<ManageClassesByTeacher />}
        />
        <Route
          path="/teachers/manage-subjects-access/:teacher_id"
          element={<ManageSubjectsByTeacher />}
        />
        <Route
          path="/students/manage-classes-access/:student_id"
          element={<ManageClassesByStudent />}
        />
        <Route
          path="/students/manage-subjects-access/:student_id"
          element={<ManageSubjectsByStudent />}
        />
        <Route
          path="/students/manage-teachers-access/:student_id"
          element={<ManageTeachersByStudent />}
        />
        <Route path="/class-room/:conversation_id" element={<Chatting />} />
        <Route path="/student-reports" element={<StudentReports />} />{" "}
        <Route path="/feeds" element={<Feeds />} />
        <Route path="/feeds-list" element={<Feeds />} />
        <Route path="/to-do-lists" element={<ToDoLists />} />
        <Route path="/personal-notes" element={<PersonalNotes />} />
        <Route path="/general-setting" element={<StudentPortalSetting />} />
        <Route path="/reported-feeds" element={<ReportedFeeds />} />
        <Route path="/email-setting" element={<EmailSetting />} />
        <Route
          path="/teacher-portal-setting"
          element={<TeacherPortalSetting />}
        />
        <Route path="/library/categories" element={<Navigate to="/library/books" replace />} />
        <Route path="/library/books" element={<BooksList />} />
        <Route path="/library/books/:bookId" element={<BookDetail />} />
        {/* Certificates */}
        <Route element={<CertificateFeatureRouteGuard />}>
        <Route path="/certificate-templates" element={<CertificateTemplates />} />
        <Route path="/certificate-templates/new" element={<CertificateTemplateForm />} />
        <Route path="/certificate-templates/:id/edit" element={<CertificateTemplateForm />} />
        <Route path="/certificate-templates/issue" element={<CertificateIssue />} />
        <Route path="/certificate-templates/issue/:id" element={<CertificateIssue />} />
        <Route path="/certificates/issue" element={<CertificateIssue />} />
        <Route path="/certificates/issue/:id" element={<CertificateIssue />} />
        <Route path="/certificates/bulk" element={<CertificatesBulkIssue />} />
        <Route path="/certificates" element={<CertificatesList />} />
        <Route path="/my-certificates" element={<MyCertificates />} />
        </Route>
      </Route>
      </Route>
      <Route path="/examination/start/:exam_id" element={<Examination />} />
      <Route path="/examination/result/:exam_id" element={<ExamResult />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/verify/:token" element={<VerifyCertificate />} />
      <Route path="/verify" element={<VerifyCertificate />} />
      <Route element={<LogoOnlyLayout />}>
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<LoginRedirect />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}
